<div class="page-container">
  <!-- Page Header -->
  <div class="page-header">
    <div class="page-header__title">
      <h1>Sign Requests</h1>
      <p class="page-header__subtitle">
        Manage and track all sign board requests
      </p>
    </div>
    <div class="page-header__actions">
      <button
        mat-stroked-button
        color="primary"
        (click)="fetchItems(pageSize, pageIndex)">
        <mat-icon>refresh</mat-icon>
        Refresh
      </button>
    </div>
  </div>

  <!-- Filters Section -->
  <form [formGroup]="form" autocomplete="off" class="filters-section">
    <div class="filters-row">
      <!-- Search Input -->
      <mat-form-field
        appearance="outline"
        class="filter-field filter-field--search">
        <mat-label>Search</mat-label>
        <mat-icon matPrefix>search</mat-icon>
        <input
          matInput
          [(ngModel)]="searchQuery"
          [ngModelOptions]="{ standalone: true }"
          placeholder="Address, postal code..."
          (keydown.enter)="$event.preventDefault(); handleSearch()" />
        <button
          *ngIf="searchQuery"
          matSuffix
          mat-icon-button
          aria-label="Clear"
          (click)="clearSearch()">
          <mat-icon>close</mat-icon>
        </button>
      </mat-form-field>

      <!-- Status Filter -->
      <mat-form-field appearance="outline" class="filter-field">
        <mat-label>Status</mat-label>
        <mat-select
          [(value)]="filterStatus"
          (valueChange)="handleStatusChanged($event)">
          <mat-option value="null">All Statuses</mat-option>
          <mat-option value="pending">
            <span class="status-option status-option--pending"></span>
            Pending
          </mat-option>
          <mat-option value="scheduled">
            <span class="status-option status-option--scheduled"></span>
            Scheduled
          </mat-option>
          <mat-option value="completed">
            <span class="status-option status-option--completed"></span>
            Completed
          </mat-option>
          <mat-option value="rejected">
            <span class="status-option status-option--rejected"></span>
            Rejected
          </mat-option>
        </mat-select>
      </mat-form-field>

      <!-- Assigned To Filter -->
      <mat-form-field appearance="outline" class="filter-field">
        <mat-label>Assigned To</mat-label>
        <mat-select
          [(value)]="filterScheduledTo"
          (valueChange)="handleStatusChanged($event)">
          <mat-option value="">All Assignees</mat-option>
          <mat-option *ngFor="let user of admins" [value]="user.id">
            {{ user.name }}
          </mat-option>
        </mat-select>
      </mat-form-field>

      <!-- Action Type Filter -->
      <mat-form-field appearance="outline" class="filter-field">
        <mat-label>Action Type</mat-label>
        <mat-select
          [(value)]="filterActionType"
          (valueChange)="handleStatusChanged($event)">
          <mat-option value="">All Actions</mat-option>
          <mat-option *ngFor="let action of actions" [value]="action.id">
            {{ action.description }}
          </mat-option>
        </mat-select>
      </mat-form-field>

      <!-- Date Range - Received -->
      <mat-form-field
        appearance="outline"
        class="filter-field filter-field--date-range"
        subscriptSizing="dynamic">
        <mat-label>Received</mat-label>
        <mat-date-range-input
          [formGroup]="form"
          [rangePicker]="receveidAtPicker">
          <input
            matStartDate
            formControlName="receveidAtStart"
            placeholder="From" />
          <input
            matEndDate
            formControlName="receveidAtEnd"
            placeholder="To"
            (dateChange)="handleDateChanged($event, 'received')" />
        </mat-date-range-input>
        <mat-datepicker-toggle
          matIconSuffix
          [for]="receveidAtPicker"></mat-datepicker-toggle>
        <mat-date-range-picker #receveidAtPicker></mat-date-range-picker>
      </mat-form-field>

      <!-- Date Range - Completed -->
      <mat-form-field
        appearance="outline"
        class="filter-field filter-field--date-range"
        subscriptSizing="dynamic">
        <mat-label>Completed</mat-label>
        <mat-date-range-input
          [formGroup]="form"
          [rangePicker]="completedAtPicker">
          <input
            matStartDate
            formControlName="completedAtStart"
            placeholder="From" />
          <input
            matEndDate
            formControlName="completedAtEnd"
            placeholder="To"
            (dateChange)="handleDateChanged($event, 'completed')" />
        </mat-date-range-input>
        <mat-datepicker-toggle
          matIconSuffix
          [for]="completedAtPicker"></mat-datepicker-toggle>
        <mat-date-range-picker #completedAtPicker></mat-date-range-picker>
      </mat-form-field>

      <!-- Clear Filters Button -->
      <button
        mat-stroked-button
        class="clear-filters-btn"
        (click)="clearFilters()"
        *ngIf="hasActiveFilters()">
        <mat-icon>filter_alt_off</mat-icon>
        Clear Filters
      </button>
    </div>

    <!-- Active Filters Summary -->
    <div class="active-filters" *ngIf="hasActiveFilters()">
      <span class="active-filters__label">Active filters:</span>
      <mat-chip-list>
        <mat-chip
          *ngIf="filterStatus && filterStatus !== 'null'"
          [removable]="true"
          (removed)="removeFilter('status')">
          Status: {{ filterStatus }}
          <mat-icon matChipRemove>cancel</mat-icon>
        </mat-chip>
        <mat-chip
          *ngIf="filterScheduledTo"
          [removable]="true"
          (removed)="removeFilter('assignee')">
          Assignee: {{ getAssigneeName(filterScheduledTo) }}
          <mat-icon matChipRemove>cancel</mat-icon>
        </mat-chip>
        <mat-chip
          *ngIf="filterActionType"
          [removable]="true"
          (removed)="removeFilter('action')">
          Action: {{ getActionName(filterActionType) }}
          <mat-icon matChipRemove>cancel</mat-icon>
        </mat-chip>
      </mat-chip-list>
    </div>
  </form>

  <!-- Results Summary -->
  <div class="results-summary" *ngIf="!isLoading">
    <span class="results-summary__count">{{ length }} results</span>
    <span class="results-summary__page" *ngIf="length > 0">
      Showing {{ pageIndex * pageSize + 1 }} -
      {{ min((pageIndex + 1) * pageSize, length) }}
    </span>
  </div>

  <!-- Loading State -->
  <div class="loading-state" *ngIf="isLoading">
    <mat-spinner diameter="48"></mat-spinner>
    <p>Loading sign requests...</p>
  </div>

  <!-- Empty State -->
  <div class="empty-state" *ngIf="!isLoading && dataSource.length === 0">
    <mat-icon>inbox</mat-icon>
    <h3>No sign requests found</h3>
    <p *ngIf="hasActiveFilters()">
      Try adjusting your filters to see more results.
    </p>
    <p *ngIf="!hasActiveFilters()">There are no sign requests at the moment.</p>
    <button
      mat-stroked-button
      color="primary"
      *ngIf="hasActiveFilters()"
      (click)="clearFilters()">
      Clear Filters
    </button>
  </div>

  <!-- Data Table -->
  <div class="table-container" *ngIf="!isLoading && dataSource.length > 0">
    <table
      mat-table
      [dataSource]="dataSource"
      matSort
      [matSortActive]="sort.active"
      [matSortDirection]="sort.direction"
      (matSortChange)="announceSortChange($event)"
      class="signs-table mat-elevation-z2">
      <!-- House Number Column -->
      <ng-container matColumnDef="board.houseNumber">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>House #</th>
        <td mat-cell *matCellDef="let element">
          {{ element.board?.houseNumber }}
        </td>
        <td
          mat-footer-cell
          *matFooterCellDef
          [attr.colspan]="displayedColumns.length">
          <mat-paginator
            #paginator
            class="table-paginator"
            (page)="handlePageEvent($event)"
            [length]="length"
            [pageSize]="pageSize"
            [disabled]="disabled"
            [showFirstLastButtons]="showFirstLastButtons"
            [pageSizeOptions]="showPageSizeOptions ? pageSizeOptions : []"
            [hidePageSize]="hidePageSize"
            [pageIndex]="pageIndex"
            aria-label="Select page">
          </mat-paginator>
        </td>
      </ng-container>

      <!-- Street Column -->
      <ng-container matColumnDef="board.address">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Street</th>
        <td mat-cell *matCellDef="let element" class="cell-address">
          <span
            [matTooltip]="element.board?.address"
            matTooltipPosition="above">
            {{ element.board?.address }}
          </span>
        </td>
      </ng-container>

      <!-- City Column -->
      <ng-container matColumnDef="board.city">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>City</th>
        <td mat-cell *matCellDef="let element">{{ element.board?.city }}</td>
      </ng-container>

      <!-- Postal Code Column -->
      <ng-container matColumnDef="board.postalCode">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Postal Code</th>
        <td mat-cell *matCellDef="let element" class="cell-mono">
          {{ element.board?.postalCode }}
        </td>
      </ng-container>

      <!-- Number of Boards Column -->
      <ng-container matColumnDef="numBoards">
        <th
          mat-header-cell
          *matHeaderCellDef
          mat-sort-header
          class="cell-center">
          Boards
        </th>
        <td mat-cell *matCellDef="let element" class="cell-center">
          <span class="board-count">{{ element.numBoards }}</span>
        </td>
      </ng-container>

      <!-- Action Column -->
      <ng-container matColumnDef="actionRequested">
        <th mat-header-cell *matHeaderCellDef>Action</th>
        <td mat-cell *matCellDef="let element">
          <span
            class="action-badge action-badge--{{
              element.actionRequested?.toLowerCase()
            }}">
            {{ element.actionRequested | prettyActionName }}
          </span>
        </td>
      </ng-container>

      <!-- To Read Column -->
      <ng-container matColumnDef="toRead.description">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>To Read</th>
        <td mat-cell *matCellDef="let element">
          {{ element.toRead | prettyReadSign }}
        </td>
      </ng-container>

      <!-- Status Column -->
      <ng-container matColumnDef="status">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Status</th>
        <td mat-cell *matCellDef="let element">
          <span class="status-badge status-badge--{{ element.status }}">
            {{ element.status }}
          </span>
        </td>
      </ng-container>

      <!-- Branch Column -->
      <ng-container matColumnDef="branch.name">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Branch</th>
        <td mat-cell *matCellDef="let element">
          <span
            [matTooltip]="element.board?.branch | getUserName"
            matTooltipPosition="above">
            {{ element.board?.branch | getUserName | slice : 0 : 12
            }}{{
              (element.board?.branch | getUserName).length > 12 ? '...' : ''
            }}
          </span>
        </td>
      </ng-container>

      <!-- Assigned To Column -->
      <ng-container matColumnDef="ba.scheduleTo">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Assigned To</th>
        <td mat-cell *matCellDef="let element">
          <span
            *ngIf="element.scheduleTo | getUserName"
            class="assignee-name"
            [matTooltip]="element.scheduleTo | getUserName"
            matTooltipPosition="above">
            {{ element.scheduleTo | getUserName | slice : 0 : 10
            }}{{ (element.scheduleTo | getUserName).length > 10 ? '...' : '' }}
          </span>
          <span *ngIf="!(element.scheduleTo | getUserName)" class="unassigned"
            >—</span
          >
        </td>
      </ng-container>

      <!-- Received At Column -->
      <ng-container matColumnDef="receivedAt">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Received</th>
        <td mat-cell *matCellDef="let element" class="cell-date">
          {{ element.receivedAt | dateGBFormatter }}
        </td>
      </ng-container>

      <!-- Scheduled At Column -->
      <ng-container matColumnDef="scheduledAt">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Scheduled</th>
        <td mat-cell *matCellDef="let element" class="cell-date">
          {{ element.scheduledAt | dateGBFormatter }}
        </td>
      </ng-container>

      <!-- Completed At Column -->
      <ng-container matColumnDef="completedAt">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Completed</th>
        <td mat-cell *matCellDef="let element" class="cell-date">
          {{ element.completedAt | dateGBFormatter }}
        </td>
      </ng-container>

      <!-- Notes Column -->
      <ng-container matColumnDef="note">
        <th mat-header-cell *matHeaderCellDef>Notes</th>
        <td mat-cell *matCellDef="let element" class="cell-notes">
          <span
            *ngIf="element.notes"
            [matTooltip]="element.notes"
            matTooltipPosition="above"
            class="notes-indicator">
            <mat-icon>sticky_note_2</mat-icon>
          </span>
        </td>
      </ng-container>

      <!-- Admin Notes Column -->
      <ng-container matColumnDef="adminNotes">
        <th mat-header-cell *matHeaderCellDef>Admin Notes</th>
        <td mat-cell *matCellDef="let element" class="cell-notes">
          <span
            *ngIf="element.adminNotes"
            [matTooltip]="element.adminNotes"
            matTooltipPosition="above"
            class="notes-indicator notes-indicator--admin">
            <mat-icon>note_alt</mat-icon>
          </span>
        </td>
      </ng-container>

      <!-- Delete Column -->
      <ng-container matColumnDef="delete">
        <th mat-header-cell *matHeaderCellDef class="cell-actions">Actions</th>
        <td mat-cell *matCellDef="let element" class="cell-actions">
          <button
            mat-icon-button
            color="warn"
            (click)="openDialog(element.board.id, '0ms', '0ms')"
            matTooltip="Delete"
            matTooltipPosition="above">
            <mat-icon>delete_outline</mat-icon>
          </button>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
      <tr
        mat-row
        *matRowDef="let row; columns: displayedColumns"
        [class.row-pending]="row.status === 'pending'"
        [class.row-scheduled]="row.status === 'scheduled'"
        [class.row-completed]="row.status === 'completed'"
        [class.row-rejected]="row.status === 'rejected'"></tr>
      <tr
        mat-footer-row
        *matFooterRowDef="displayedColumnsFooter; sticky: true"></tr>
    </table>
  </div>
</div>
